import type { CatalogSizingOption } from '@/services/iem/types';
import type {
  IssueAdditionalCartItem,
  IssueBuyProgramCartItem,
  IssuePlannerCartItem,
} from './types';

export interface IssueSizingSelectionCandidate {
  parentCatalogId: string;
  catalogId: string;
  itemName: string;
  sizeLabel?: string;
  quantity: number;
}

interface RawSizingSelectionCandidate {
  parentCatalogId?: string | null;
  catalogId?: string | null;
  itemName?: string | null;
  sizeLabel?: string | null;
  quantity?: number;
}

const normalizeText = (value: string | null | undefined): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeQuantity = (value: number | undefined): number => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return 1;
  }

  return Math.max(1, Math.trunc(value));
};

const coerceCandidate = (
  value: RawSizingSelectionCandidate,
): IssueSizingSelectionCandidate | null => {
  const parentCatalogId = normalizeText(value.parentCatalogId);
  const catalogId = normalizeText(value.catalogId);
  const itemName = normalizeText(value.itemName);

  if (!parentCatalogId || !catalogId || !itemName || parentCatalogId === catalogId) {
    return null;
  }

  return {
    parentCatalogId,
    catalogId,
    itemName,
    sizeLabel: normalizeText(value.sizeLabel),
    quantity: normalizeQuantity(value.quantity),
  };
};

const dedupeSizingCandidates = (
  candidates: RawSizingSelectionCandidate[],
): IssueSizingSelectionCandidate[] => {
  const byParentCatalogId = new Map<string, IssueSizingSelectionCandidate>();

  for (const candidate of candidates) {
    const normalized = coerceCandidate(candidate);
    if (!normalized) {
      continue;
    }

    const existing = byParentCatalogId.get(normalized.parentCatalogId);
    if (existing && existing.catalogId === normalized.catalogId) {
      byParentCatalogId.set(normalized.parentCatalogId, {
        ...normalized,
        quantity: existing.quantity + normalized.quantity,
      });
      continue;
    }

    byParentCatalogId.set(normalized.parentCatalogId, normalized);
  }

  return Array.from(byParentCatalogId.values());
};

export const buildIssueSizingSelectionCandidates = (params: {
  ieCartItems: IssuePlannerCartItem[];
  additionalCartItems: IssueAdditionalCartItem[];
  buyProgramCartItems: IssueBuyProgramCartItem[];
  catalogChildren: CatalogSizingOption[];
}): IssueSizingSelectionCandidate[] => {
  const childCatalogById = new Map<string, CatalogSizingOption>();
  params.catalogChildren.forEach((child) => {
    childCatalogById.set(child.id, child);
  });

  return dedupeSizingCandidates([
    ...params.ieCartItems.map((item) => {
      const catalogId = normalizeText(item.selectedCatalogId);
      const childCatalog = catalogId ? childCatalogById.get(catalogId) : undefined;
      return {
        parentCatalogId: childCatalog?.parentCatalogId,
        catalogId,
        itemName: item.name,
        sizeLabel: normalizeText(item.sizeLabel) ?? normalizeText(childCatalog?.sizeLabel),
        quantity: item.assetIds.length,
      };
    }),
    ...params.additionalCartItems.map((item) => {
      const catalogId = normalizeText(item.groupId);
      const childCatalog = catalogId ? childCatalogById.get(catalogId) : undefined;
      return {
        parentCatalogId: childCatalog?.parentCatalogId,
        catalogId,
        itemName: item.name,
        sizeLabel: normalizeText(item.sizeLabel) ?? normalizeText(childCatalog?.sizeLabel),
        quantity: item.assetIds.length,
      };
    }),
    ...params.buyProgramCartItems.map((item) => {
      const catalogId = normalizeText(item.catalog.id);
      const parentCatalogId =
        normalizeText(item.catalog.entityData?.parent_id as string | undefined) ??
        (catalogId ? childCatalogById.get(catalogId)?.parentCatalogId : undefined);
      const childCatalog = catalogId ? childCatalogById.get(catalogId) : undefined;
      return {
        parentCatalogId,
        catalogId,
        itemName: item.catalog.name,
        sizeLabel:
          normalizeText(item.catalog.entityData?.size as string | undefined) ??
          normalizeText(childCatalog?.sizeLabel),
        quantity: item.quantity,
      };
    }),
  ]);
};
